import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const Home = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();

  const handleSearch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const results = await api.search(searchQuery);
      setSearchResults(results);
      setShowDropdown(true);
    } catch (err) {
      console.error('Search error:', err);
      setError('Search failed. Please try again.');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  // Debounced search
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (searchQuery.length >= 2) {
        handleSearch();
      } else {
        setSearchResults([]);
        setShowDropdown(false);
      }
    }, 300);

    return () => clearTimeout(delaySearch);
  }, [searchQuery, handleSearch]);

  const handleSelectCompany = async (result) => {
    setSearchQuery(`${result.sponsor_name} (${result.ein})`);
    setShowDropdown(false);
    setLoading(true);
    setError(null);

    try {
      const planData = await api.getPlans(result.ein);
      setPlans(planData);
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to load plans. Please try again.');
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (plan) => {
    navigate(`/plan-analysis/${plan.ein}/${plan.plan_number}/${plan.plan_year}`);
  };

  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US').format(value);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'white' }}>
      {/* Header - Minimal & Clean */}
      <header style={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '1rem 0'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1e3a8a', letterSpacing: '0.02em' }}>
              Love Your 401k
            </div>
            <div style={{ display: 'flex', gap: '2.5rem', alignItems: 'center' }}>
              <a href="/" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>Search Plans</a>
              <a href="/estimator" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>What Should I Pay for a 401(k)?</a>
              <a href="/faq" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>FAQ</a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Bold & Spacious */}
      <section style={{
        padding: '5rem 0 3rem 0',
        background: 'white'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2.5rem' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            {/* Main Headline */}
            <h1 style={{
              fontSize: '3rem',
              fontWeight: 900,
              color: '#111827',
              marginBottom: '1.5rem',
              lineHeight: 1.1,
              letterSpacing: '-0.02em'
            }}>
              Find out what you're really paying for your 401(k)
            </h1>

            {/* Subheadline */}
            <p style={{
              fontSize: '1.125rem',
              color: '#6b7280',
              marginBottom: '2.5rem',
              lineHeight: 1.6
            }}>
              Analyze millions of 401(k) plans. Benchmark costs. Identify savings opportunities.
            </p>

            {/* Search Box - Large & Prominent */}
            <div style={{ position: 'relative', maxWidth: '700px', margin: '0 auto' }}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Enter company name or EIN..."
                style={{
                  width: '100%',
                  padding: '1rem 1.5rem',
                  fontSize: '1rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  outline: 'none',
                  transition: 'all 0.2s',
                  boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#1e3a8a';
                  e.target.style.boxShadow = '0 0 0 3px rgba(30, 58, 138, 0.1)';
                  if (searchResults.length > 0) setShowDropdown(true);
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = '0 1px 2px 0 rgb(0 0 0 / 0.05)';
                  setTimeout(() => setShowDropdown(false), 200);
                }}
              />
              
              {loading && (
                <div style={{ position: 'absolute', right: '1.5rem', top: '50%', transform: 'translateY(-50%)' }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid #1e3a8a',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 0.6s linear infinite'
                  }}></div>
                </div>
              )}

              {/* Autocomplete Dropdown */}
              {showDropdown && searchResults.length > 0 && (
                <div style={{
                  position: 'absolute',
                  width: '100%',
                  marginTop: '0.5rem',
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  zIndex: 50
                }}>
                  {searchResults.map((result, index) => (
                    <div
                      key={`${result.ein}-${index}`}
                      onClick={() => handleSelectCompany(result)}
                      style={{
                        padding: '1rem 1.5rem',
                        cursor: 'pointer',
                        borderBottom: index < searchResults.length - 1 ? '1px solid #f3f4f6' : 'none',
                        transition: 'background 0.15s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                    >
                      <div style={{ fontWeight: 600, color: '#111827', marginBottom: '0.25rem' }}>
                        {result.sponsor_name}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        EIN: {result.ein}
                        {result.city && result.state && (
                          <span style={{ marginLeft: '0.75rem' }}>
                            • {result.city}, {result.state}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {error && (
              <div style={{
                marginTop: '1.5rem',
                padding: '1rem',
                background: '#fee2e2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                color: '#991b1b',
                fontSize: '0.875rem'
              }}>
                {error}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Plans List - If Search Results */}
      {plans.length > 0 && (
        <section style={{ padding: '2rem 0 3rem 0', background: 'white' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2.5rem' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#111827', marginBottom: '0.25rem', letterSpacing: '-0.01em' }}>
                Plans for {plans[0].sponsor_name}
              </h2>
              <p style={{ fontSize: '1rem', color: '#6b7280' }}>
                Select a plan to view comprehensive analysis
              </p>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {plans.map((plan, index) => (
                <div
                  key={`${plan.ein}-${plan.plan_number}-${plan.plan_year}-${index}`}
                  style={{
                    background: 'white',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 10px 15px -3px rgb(0 0 0 / 0.1)';
                    e.currentTarget.style.borderColor = '#1e3a8a';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '2rem' }}>
                    <div style={{ flex: 1 }}>
                      {/* Plan Name */}
                      <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '1rem', letterSpacing: '-0.01em' }}>
                        {plan.plan_name || `Plan ${plan.plan_number}`}
                      </h3>
                      
                      {/* Key Metrics Grid */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                          <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                            EIN
                          </div>
                          <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#111827' }}>
                            {plan.ein}
                          </div>
                        </div>
                        
                        <div>
                          <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                            Plan Number
                          </div>
                          <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#111827' }}>
                            {plan.plan_number}
                          </div>
                        </div>
                        
                        <div>
                          <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                            Plan Year
                          </div>
                          <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#111827' }}>
                            {plan.plan_year}
                          </div>
                        </div>
                        
                        <div>
                          <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                            Participants
                          </div>
                          <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#111827' }}>
                            {formatNumber(plan.participants_with_balance || plan.participants)}
                          </div>
                        </div>
                      </div>

                      {/* Assets & Location */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div style={{
                          background: '#eff6ff',
                          padding: '1rem',
                          borderRadius: '8px',
                          border: '1px solid #dbeafe'
                        }}>
                          <div style={{ fontSize: '0.6875rem', color: '#1e3a8a', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                            Total Assets
                          </div>
                          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e3a8a', letterSpacing: '-0.01em' }}>
                            {formatCurrency(plan.assets)}
                          </div>
                        </div>

                        {plan.city && plan.state ? (
                          <div style={{
                            background: '#fafbfc',
                            padding: '1rem',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb'
                          }}>
                            <div style={{ fontSize: '0.6875rem', color: '#6b7280', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                              Location
                            </div>
                            <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#111827' }}>
                              {plan.city}, {plan.state}
                            </div>
                          </div>
                        ) : (
                          <div></div>
                        )}
                      </div>
                    </div>
                    
                    {/* CTA Button */}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <button
                        onClick={() => handleViewDetails(plan)}
                        style={{
                          padding: '0.875rem 1.5rem',
                          background: '#1e3a8a',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '0.9375rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#1e40af'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#1e3a8a'}
                      >
                        View Analysis →
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Empty State - When No Search */}
      {!loading && plans.length === 0 && !error && (
        <section style={{ padding: '2rem 0', background: '#fafbfc' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2.5rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '2rem', maxWidth: '1000px', margin: '0 auto' }}>
              {/* Have a 401(k)? */}
              <div style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  margin: '0 auto 1.5rem',
                  background: '#eff6ff',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg
                    style={{ width: '32px', height: '32px', color: '#1e3a8a' }}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '0.75rem' }}>
                  Have a 401(k)?
                </h3>
                <p style={{ fontSize: '0.9375rem', color: '#6b7280', lineHeight: 1.6 }}>
                  Enter your company name or EIN above to unlock comprehensive cost analysis and peer benchmarking.
                </p>
              </div>

              {/* Looking for a 401(k)? */}
              <div style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  margin: '0 auto 1.5rem',
                  background: '#f3f4f6',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg
                    style={{ width: '32px', height: '32px', color: '#6b7280' }}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '0.75rem' }}>
                  Looking for a 401(k)?
                </h3>
                <p style={{ fontSize: '0.9375rem', color: '#6b7280', lineHeight: 1.6 }}>
                  Use the "What Should I Pay for a 401(k)?" link above to see what similar companies are paying before you buy a plan.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer style={{ background: '#111827', color: 'white', padding: '2rem 0', marginTop: '3rem' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2.5rem' }}>
          <div style={{ textAlign: 'center' }}>
            <h4 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>Love Your 401k</h4>
            <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
              Powered by Form 5500 data • Helping employees make better retirement decisions
            </p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Home;
